import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  CheckCircle, Warning, ArrowsClockwise, Lightning,
  ShieldCheck, EnvelopeSimple, FolderSimple, Link, LinkBreak, Plugs, Receipt,
} from "@phosphor-icons/react";
import { Button } from "../components/ui/button";
import { getMsStatus, getMsAuthorizeUrl, disconnectMs } from "../api/microsoft";
import { getConnectorStatus, initiateConnectorAuth, disconnectConnector } from "../api/automations";
import { getXeroStatus, getXeroConnectUrl, disconnectXero } from "../api/xero";

// ── Integration catalogue ──────────────────────────────────────────────────────

const INTEGRATIONS = [
  {
    id: "microsoft",
    name: "Microsoft Account",
    provider: "Microsoft Graph API",
    icon: ShieldCheck,
    accent: "bg-blue-50 border-blue-200 text-blue-600",
    iconBg: "bg-blue-100",
    connectedBadge: "bg-blue-50 text-blue-700 border-blue-200",
    description:
      "Server-side OAuth for your Microsoft account. Required for browsing OneDrive, Smart Upload, and the Email Project Router.",
    features: [
      "Browse & upload files in Explore Files",
      "Smart Upload — AI file sorting",
      "Email Project Router",
      "Automatic drawing version archiving",
    ],
    type: "ms",
  },
  {
    id: "outlook",
    name: "Outlook Email",
    provider: "Azure Logic Apps",
    icon: EnvelopeSimple,
    accent: "bg-violet-50 border-violet-200 text-violet-600",
    iconBg: "bg-violet-100",
    connectedBadge: "bg-violet-50 text-violet-700 border-violet-200",
    description:
      "Connects AI Automations to your Outlook inbox for scheduled sends and email-triggered workflows.",
    features: [
      "Send scheduled emails to any recipient",
      "Trigger automations when emails arrive",
    ],
    type: "connector",
    connectorType: "office365",
  },
  {
    id: "onedrive",
    name: "OneDrive Files",
    provider: "Azure Logic Apps",
    icon: FolderSimple,
    accent: "bg-emerald-50 border-emerald-200 text-emerald-600",
    iconBg: "bg-emerald-100",
    connectedBadge: "bg-emerald-50 text-emerald-700 border-emerald-200",
    description:
      "Lets AI Automations monitor your OneDrive folders and react to file upload events.",
    features: [
      "Get notified when files are uploaded",
      "Save email attachments to project folders",
    ],
    type: "connector",
    connectorType: "onedriveforbusiness",
  },
  {
    id: "xero",
    name: "Xero Accounting",
    provider: "Xero API",
    icon: Receipt,
    accent: "bg-cyan-50 border-cyan-200 text-cyan-600",
    iconBg: "bg-cyan-100",
    connectedBadge: "bg-cyan-50 text-cyan-700 border-cyan-200",
    description:
      "Server-side OAuth for your Xero organisation. Required for automated invoicing and payment sync when a project's status changes to Ready for Invoice.",
    features: [
      "Auto-creates the Xero Contact for the client",
      "Auto-generates the ACCREC invoice on 'Ready for Invoice'",
      "Payment webhook marks the project Completed: Invoice Paid",
    ],
    type: "xero",
  },
];

// ── Status helpers ─────────────────────────────────────────────────────────────

function StatusChip({ connected, needsReauth }) {
  if (needsReauth)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold border rounded-full bg-amber-50 text-amber-700 border-amber-200">
        <Warning size={9} weight="fill" /> Needs reconnection
      </span>
    );
  if (connected)
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold border rounded-full bg-emerald-50 text-emerald-700 border-emerald-200">
        <CheckCircle size={9} weight="fill" /> Connected
      </span>
    );
  return (
    <span className="inline-flex items-center gap-1 px-2 py-0.5 text-[10px] font-semibold border rounded-full bg-slate-100 text-slate-500 border-slate-200">
      Not connected
    </span>
  );
}

// ── Single integration card ────────────────────────────────────────────────────

function IntegrationCard({ integration, status, onConnect, onDisconnect, busy }) {
  const { id, name, provider, icon: Icon, iconBg, description, features, type } = integration;
  const connected   = status?.connected;
  const needsReauth = status?.needs_reauth;
  const isBusy      = busy === id;

  return (
    <div
      className={`bg-white border rounded-sm flex flex-col transition-shadow hover:shadow-sm ${
        connected && !needsReauth ? "border-slate-200" : needsReauth ? "border-amber-200" : "border-slate-200"
      }`}
      data-testid={`integration-card-${id}`}
    >
      {/* Card header */}
      <div className="p-5 flex items-start gap-3 border-b border-slate-100">
        <div className={`w-10 h-10 rounded-sm flex items-center justify-center flex-shrink-0 ${iconBg}`}>
          <Icon size={18} className={connected ? "text-slate-700" : "text-slate-500"} />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <h3 className="text-sm font-bold text-slate-900">{name}</h3>
            <StatusChip connected={connected} needsReauth={needsReauth} />
          </div>
          <p className="text-[10px] text-slate-400 font-medium mt-0.5">{provider}</p>
        </div>
      </div>

      {/* Card body */}
      <div className="p-5 flex-1 space-y-4">
        <p className="text-xs text-slate-600 leading-relaxed">{description}</p>

        <div>
          <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-2">Enables</p>
          <ul className="space-y-1">
            {features.map((f) => (
              <li key={f} className="flex items-start gap-2 text-xs text-slate-600">
                <CheckCircle size={12} className="text-emerald-500 flex-shrink-0 mt-0.5" weight="fill" />
                {f}
              </li>
            ))}
          </ul>
        </div>
      </div>

      {/* Card footer */}
      <div className="px-5 py-4 border-t border-slate-100 flex items-center justify-between bg-slate-50/60 rounded-b-sm">
        {connected && !needsReauth ? (
          <>
            <div className="flex flex-col gap-0.5 min-w-0">
              <div className="flex items-center gap-1.5 text-emerald-600">
                <CheckCircle size={13} weight="fill" />
                <span className="text-xs font-medium">Active</span>
              </div>
              {id === "microsoft" && status?.ms_account_email && (
                <span className="text-[10px] text-slate-400 truncate max-w-[200px]" title={status.ms_account_email} data-testid="ms-account-email-badge">
                  {status.ms_display_name ? `${status.ms_display_name} · ` : ""}{status.ms_account_email}
                </span>
              )}
            </div>
            {(type === "ms" || type === "connector" || type === "xero") && (
              <Button
                size="sm"
                variant="outline"
                onClick={() => onDisconnect(id)}
                disabled={isBusy}
                className="text-xs text-slate-500 hover:text-red-600 hover:border-red-200"
                data-testid={`disconnect-${id}-btn`}
              >
                {isBusy
                  ? <ArrowsClockwise size={11} className="animate-spin mr-1.5" />
                  : <LinkBreak size={11} className="mr-1.5" />}
                Disconnect
              </Button>
            )}
          </>
        ) : (
          <>
            <p className="text-xs text-slate-400">
              {needsReauth ? "Session expired" : "Not yet authorized"}
            </p>
            <Button
              size="sm"
              onClick={() => onConnect(id)}
              disabled={isBusy}
              className={`text-xs ${
                needsReauth
                  ? "bg-amber-500 hover:bg-amber-600 text-white border-amber-500"
                  : "bg-primary hover:bg-primary/90 text-white"
              }`}
              data-testid={`connect-${id}-btn`}
            >
              {isBusy
                ? <ArrowsClockwise size={11} className="animate-spin mr-1.5" />
                : <Lightning size={11} className="mr-1.5" weight="fill" />}
              {needsReauth ? "Reconnect" : "Connect"}
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ── Summary banner ─────────────────────────────────────────────────────────────

function SummaryBanner({ statuses }) {
  const total     = INTEGRATIONS.length;
  const connected = INTEGRATIONS.filter((i) => statuses[i.id]?.connected && !statuses[i.id]?.needs_reauth).length;
  const reauth    = INTEGRATIONS.filter((i) => statuses[i.id]?.needs_reauth).length;
  const allDone   = connected === total;

  if (allDone)
    return (
      <div className="flex items-center gap-3 bg-emerald-50 border border-emerald-200 rounded-sm px-5 py-3.5" data-testid="integrations-banner-all-connected">
        <CheckCircle size={16} className="text-emerald-600 flex-shrink-0" weight="fill" />
        <p className="text-sm font-medium text-emerald-800">
          All {total} integrations connected — you're fully set up.
        </p>
      </div>
    );

  if (reauth > 0)
    return (
      <div className="flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-sm px-5 py-3.5" data-testid="integrations-banner-reauth">
        <Warning size={16} className="text-amber-600 flex-shrink-0" weight="fill" />
        <p className="text-sm font-medium text-amber-800">
          {reauth} integration{reauth > 1 ? "s need" : " needs"} reconnection.
        </p>
      </div>
    );

  return (
    <div className="flex items-center gap-3 bg-slate-50 border border-slate-200 rounded-sm px-5 py-3.5" data-testid="integrations-banner-partial">
      <Plugs size={16} className="text-slate-400 flex-shrink-0" />
      <p className="text-sm text-slate-600">
        <span className="font-semibold">{connected} of {total}</span> integrations connected.{" "}
        <span className="text-slate-500">Connect the rest to unlock all features.</span>
      </p>
    </div>
  );
}

// ── Main page ──────────────────────────────────────────────────────────────────

/**
 * IntegrationsContent — the integrations UI without the page-level header.
 * Used by the Settings page to embed integrations as a tab.
 */
export function IntegrationsContent() {
  const [statuses, setStatuses]   = useState({
    microsoft: { connected: false, needs_reauth: false },
    outlook:   { connected: false },
    onedrive:  { connected: false },
    xero:      { connected: false },
  });
  const [loading, setLoading]     = useState(true);
  const [busy, setBusy]           = useState(null);
  const pollRef                   = useRef(null);

  const loadStatuses = useCallback(async () => {
    try {
      const [msResult, connResult, xeroResult] = await Promise.allSettled([
        getMsStatus(),
        getConnectorStatus(),
        getXeroStatus(),
      ]);
      const ms   = msResult.status   === "fulfilled" ? msResult.value   : { connected: false };
      const conn = connResult.status === "fulfilled" ? connResult.value : {};
      const xero = xeroResult.status === "fulfilled" ? xeroResult.value : { connected: false };
      setStatuses({
        microsoft: { connected: !!ms.connected, needs_reauth: !!ms.needs_reauth, ms_account_email: ms.ms_account_email || "", ms_display_name: ms.ms_display_name || "" },
        outlook:   { connected: !!conn.outlook?.connected },
        onedrive:  { connected: !!conn.onedrive?.connected },
        xero:      { connected: !!xero.connected, needs_reauth: !!xero.needs_reauth },
      });
    } catch {
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadStatuses(); }, [loadStatuses]);
  useEffect(() => () => clearInterval(pollRef.current), []);

  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const xeroResult = params.get("xero");
    if (!xeroResult) return;
    if (xeroResult === "connected") toast.success("Xero connected!");
    if (xeroResult === "error") toast.error("Failed to connect Xero. Please try again.");
    window.history.replaceState({}, "", window.location.pathname);
  }, []);

  const handleConnect = useCallback(async (id) => {
    setBusy(id);
    try {
      if (id === "microsoft") {
        const authUrl = await getMsAuthorizeUrl();
        window.location.href = authUrl;
        return;
      }
      if (id === "xero") {
        window.location.href = getXeroConnectUrl();
        return;
      }
      const integration   = INTEGRATIONS.find((i) => i.id === id);
      const connectorType = integration.connectorType;
      const redirectUrl   = `${window.location.origin}/connector-callback?connector=${id}`;
      const popup = window.open("about:blank", "connector_auth", "width=650,height=750,resizable=yes,scrollbars=yes");
      if (popup) {
        popup.document.write(
          "<html><body style='font-family:sans-serif;display:flex;align-items:center;justify-content:center;height:100vh;margin:0;background:#f8fafc'><div style='text-align:center;color:#64748b'><p style='font-size:16px;font-weight:600'>Connecting to Azure…</p><p style='font-size:13px'>This may take up to 60 seconds on first use.</p></div></body></html>"
        );
      }
      let result;
      try {
        result = await initiateConnectorAuth({ connector_type: connectorType, redirect_url: redirectUrl });
      } catch (e) {
        if (popup && !popup.closed) popup.close();
        toast.error(e.response?.data?.detail || e.message || `Failed to connect ${id}.`);
        setBusy(null);
        return;
      }
      if (popup && !popup.closed) {
        popup.location.href = result.consent_url;
      } else {
        window.open(result.consent_url, "connector_auth", "width=650,height=750,resizable=yes,scrollbars=yes");
      }
      pollRef.current = setInterval(async () => {
        try {
          const connectors = await getConnectorStatus();
          const isConnected = id === "outlook" ? connectors.outlook?.connected : connectors.onedrive?.connected;
          if (isConnected || popup?.closed) {
            clearInterval(pollRef.current);
            setBusy(null);
            if (isConnected) {
              toast.success(`${id === "outlook" ? "Outlook" : "OneDrive"} connector authorized!`);
              loadStatuses();
            }
          }
        } catch (_) {}
      }, 3000);
      setTimeout(() => { clearInterval(pollRef.current); setBusy(null); }, 600000);
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message || `Failed to connect ${id}.`);
      setBusy(null);
    }
  }, [loadStatuses]);

  const handleDisconnect = useCallback(async (id) => {
    setBusy(id);
    try {
      if (id === "microsoft") { await disconnectMs(); toast.success("Microsoft account disconnected."); }
      else if (id === "xero") { await disconnectXero(); toast.success("Xero disconnected."); }
      else { await disconnectConnector(id); toast.success(`${id === "outlook" ? "Outlook Email" : "OneDrive Files"} disconnected.`); }
      loadStatuses();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to disconnect.");
    } finally {
      setBusy(null);
    }
  }, [loadStatuses]);

  return (
    <div className="space-y-6">
      {!loading && <SummaryBanner statuses={statuses} />}
      {loading ? (
        <div className="flex items-center gap-2 text-slate-400 py-12 justify-center" data-testid="integrations-loading">
          <ArrowsClockwise size={18} className="animate-spin" />
          <span className="text-sm">Checking integration status…</span>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4" data-testid="integrations-grid">
          {INTEGRATIONS.map((integration) => (
            <IntegrationCard
              key={integration.id}
              integration={integration}
              status={statuses[integration.id]}
              onConnect={handleConnect}
              onDisconnect={handleDisconnect}
              busy={busy}
            />
          ))}
        </div>
      )}
      <div className="bg-white border border-slate-200 rounded-sm px-5 py-4" data-testid="integrations-help">
        <p className="text-[10px] font-semibold uppercase tracking-wider text-slate-400 mb-3">Setup Guide</p>
        <ul className="space-y-2">
          {[
            { step: "1", text: "Connect Microsoft Account first — this uses a secure server-side OAuth flow and is required for Explore Files and Smart Upload." },
            { step: "2", text: "Authorize Outlook and OneDrive connectors — these use Azure Logic Apps and require a separate sign-in popup (same Microsoft account)." },
            { step: "3", text: "Once all three are connected, you can create AI Automations, use the Email Router, and Smart Upload files from any project." },
          ].map(({ step, text }) => (
            <li key={step} className="flex items-start gap-3 text-xs text-slate-600">
              <span className="w-5 h-5 rounded-full bg-slate-100 text-slate-500 font-bold text-[10px] flex items-center justify-center flex-shrink-0 mt-0.5">{step}</span>
              {text}
            </li>
          ))}
        </ul>
      </div>
    </div>
  );
}

/**
 * Legacy route — redirects to Settings > Integrations tab.
 * Kept so that bookmarks and OAuth callback flows still work.
 */
export default function IntegrationsPage() {
  const navigate = useNavigate();
  useEffect(() => { navigate("/settings?tab=integrations", { replace: true }); }, [navigate]);
  return null;
}
